/**
 * ChatGPT Ads (OpenAI Ads) pixel + conversion tracking.
 *
 * Pixel id, SDK url and the init snippet shape come directly from the
 * OpenAI Ads spec — see the spec file linked from the implementation plan.
 */

export const CHATGPT_ADS_PIXEL_ID = "WbH9AUQWc783QCH5H6CFj1";
export const CHATGPT_ADS_SDK_URL = "https://bzrcdn.openai.com/sdk/oaiq.min.js";

/** Raw inline `<script>` body — inject as early as possible in `<head>`. */
export function getChatGptAdsPixelScript(): string {
  return `(function (w, d, s, u) {
    if (w.oaiq) return;
    var q = function () { q.q.push(arguments); };
    q.q = [];
    w.oaiq = q;
    var js = d.createElement(s);
    js.async = true;
    js.src = u;
    var f = d.getElementsByTagName(s)[0];
    f.parentNode.insertBefore(js, f);
  })(window, document, "script", ${JSON.stringify(CHATGPT_ADS_SDK_URL)});
  oaiq("init", { pixelId: ${JSON.stringify(CHATGPT_ADS_PIXEL_ID)} });`;
}

export function parseObrefFromCookieString(cookieString: string): string | undefined {
  if (!cookieString) return undefined;
  const match = cookieString
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("__obref="));
  if (!match) return undefined;
  const raw = match.slice("__obref=".length);
  if (!raw) return undefined;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export function getObrefCookie(): string | undefined {
  if (typeof document === "undefined") return undefined;
  return parseObrefFromCookieString(document.cookie);
}

type OaiqFn = (...args: unknown[]) => void;

/** Always reads `window.oaiq` fresh — the pixel stub queues calls made before the SDK loads. */
const getOaiq = (): OaiqFn | undefined => {
  if (typeof window === "undefined") return undefined;
  return (window as unknown as { oaiq?: OaiqFn }).oaiq;
};

const generateEventId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `evt_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Repeat `oaiq("init")` with hashed email/phone so OpenAI can match the lead to the ad click.
 * Only the SHA-256 hashes leave the browser. Best-effort — never throws.
 */
export async function setChatGptAdsUser(email: string, phone: string): Promise<void> {
  try {
    const oaiq = getOaiq();
    if (!oaiq || typeof crypto === "undefined" || !crypto.subtle) return;
    oaiq("init", {
      user: {
        email_sha256: await sha256Hex(email.trim().toLowerCase()),
        // digits only, with country code, e.g. 919876543210
        phone_number_sha256: await sha256Hex(phone.replace(/[^0-9]/g, "").replace(/^0+/, "")),
      },
    });
  } catch {
    // Tracking must never break the form submit.
  }
}

/** Fires the browser conversion and returns the event_id to reuse on the server mirror. */
export function fireLeadCreated(): string {
  const eventId = generateEventId();
  getOaiq()?.("measure", "lead_created", { type: "customer_action" }, { event_id: eventId });
  return eventId;
}

/** Fires the browser conversion and returns the event_id to reuse on the server mirror. */
export function fireAppointmentScheduled(): string {
  const eventId = generateEventId();
  getOaiq()?.("measure", "appointment_scheduled", { type: "customer_action" }, { event_id: eventId });
  return eventId;
}

export function firePricingViewed(): void {
  getOaiq()?.("measure", "custom", { type: "custom" }, { custom_event_name: "pricing_viewed" });
}

export interface ServerConversionInput {
  id: string;
  type: "lead_created" | "appointment_scheduled";
  oppref?: string;
  sourceUrl: string;
  email?: string;
  phone?: string;
}

/** Best-effort mirror of a browser conversion — fire-and-forget, never blocks the caller. */
export function postServerConversion(input: ServerConversionInput): void {
  if (typeof window === "undefined") return;
  const body = JSON.stringify({ ...input, obref: getObrefCookie() });
  try {
    fetch("/api/chatgpt-ads-capi", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    })
      .then((res) => {
        if (!res.ok && process.env.NODE_ENV !== "production") {
          console.warn(`[chatgpt-ads-capi] server conversion mirror failed: HTTP ${res.status}`);
        }
      })
      .catch(() => {
        // Browser pixel event already fired; server mirror is best-effort.
      });
  } catch {
    // ignore
  }
}
